import { supabase } from '@/lib/supabase';
import { Family } from '@/types/models';
import { toast } from 'sonner';
import { BillingStatus } from '@/types/constants';

export const getFamilies = async (organizationId: string): Promise<Family[]> => {
  try {
    // The RLS policies will now automatically filter based on user's role and organization
    const { data, error } = await supabase
      .from('families')
      .select('*')
      .eq('organization_id', organizationId);

    if (error) {
      console.error('Error fetching families:', error);
      throw error;
    }
    
    return (data || []) as Family[];
  } catch (error) {
    console.error('Error fetching families:', error);
    toast.error('Failed to load families', {
      description: 'Please try again or contact support if the issue persists.'
    });
    return [];
  }
};

export const getFamily = async (familyId: string): Promise<Family | null> => {
  try {
    const { data, error } = await supabase
      .from('families')
      .select('*')
      .eq('id', familyId)
      .single();

    if (error) {
      console.error('Error fetching family:', error);
      throw error;
    }
    
    return data as Family;
  } catch (error) {
    console.error('Error fetching family:', error);
    toast.error('Failed to load family details');
    return null;
  }
};

export const getFamilyById = getFamily; // Alias for backward compatibility

export const createFamily = async (
  data: {
    name: string;
    organization_id: string;
    billing_status?: string;
    is_active?: boolean;
  }
): Promise<Family | null> => {
  try {
    // Explicitly create object without ID to ensure Supabase generates one
    const familyData = {
      name: data.name,
      organization_id: data.organization_id,
      billing_status: data.billing_status,
      is_active: data.is_active !== undefined ? data.is_active : true // Default to active
    };

    const { error } = await supabase
      .from('families')
      .upsert([familyData], { onConflict: 'organization_id,name' });

    if (error && error.code !== '23505') {
      console.error('Error creating family:', error);
      toast.error('Failed to create family', {
        description: error.message,
      });
      throw error;
    }

    // Refetch the family by name and org ID to get the new row
    const { data: families, error: fetchError } = await supabase
      .from('families')
      .select('*')
      .eq('organization_id', familyData.organization_id)
      .eq('name', familyData.name)
      .order('created_at', { ascending: false })
      .limit(1);

    if (fetchError || !families || families.length === 0) {
      throw fetchError || new Error('Failed to fetch new family');
    }
    return families[0] as Family;
  } catch (error) {
    console.error('Error creating family:', error);
    return null;
  }
};

export const updateFamily = async (
  familyId: string,
  updates: Partial<Family>
): Promise<Family | null> => {
  try {
    // Direct update with supabase client
    const { data, error } = await supabase
      .from('families')
      .update(updates)
      .eq('id', familyId)
      .select()
      .single();

    if (error) {
      console.error('Error updating family:', error);
      if (error.code === 'PGRST116') {
        toast.error('Permission denied', {
          description: 'You do not have permission to update this family.'
        });
      } else {
        toast.error('Failed to update family', {
          description: error.message
        });
      }
      throw error;
    }
    
    return data as Family;
  } catch (error) {
    console.error('Error updating family:', error);
    return null;
  }
};

export const setFamilyActiveStatus = async (
  familyId: string, 
  isActive: boolean
): Promise<Family | null> => {
  try {
    return await updateFamily(familyId, { is_active: isActive });
  } catch (error) {
    console.error('Error updating family active status:', error);
    toast.error(
      isActive ? 'Failed to activate family' : 'Failed to deactivate family',
      { description: 'Please try again or contact support.' }
    );
    return null;
  }
};

export const updateFamilyBillingStatus = async (
  familyId: string, 
  billingStatus: BillingStatus
): Promise<Family | null> => {
  try {
    return await updateFamily(familyId, { billing_status: billingStatus });
  } catch (error) {
    console.error('Error updating family billing status:', error);
    toast.error('Failed to update billing status', {
      description: 'Please try again or contact support.'
    });
    return null;
  }
};

export const deleteFamily = async (familyId: string): Promise<boolean> => {
  try {
    // Direct delete with supabase client
    const { error } = await supabase
      .from('families')
      .delete()
      .eq('id', familyId);

    if (error) {
      console.error('Error deleting family:', error);
      if (error.code === 'PGRST116') {
        toast.error('Permission denied', {
          description: 'You do not have permission to delete this family.'
        });
      } else {
        toast.error('Failed to delete family', {
          description: error.message
        });
      }
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting family:', error);
    return false;
  }
};

export const FamilyService = {
  getFamilies,
  createFamily,
  updateFamily,
  deleteFamily,
  getFamily,
  getFamilyById,
  setFamilyActiveStatus,
  updateFamilyBillingStatus, // Add the new method here
};
